<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-4">
        <h2>{{ $t('memberDataSection.societies') }}</h2>
        <v-spacer></v-spacer>
      </div>

      <v-card elevation="0" class="px-5 mt-7">
        <v-card-text>
          <v-row style="margin-left:-30px; margin-bottom: 20px; margin-top: 15px;">
            <v-col cols="12">
              <v-data-table
                :headers="tableHeaders"
                :items="societies"
                :items-per-page="options.limit"
                :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
                item-key="id"
                hide-default-footer
                :loading="loading"
                :options.sync="options"
                :server-items-length="totalSocieties"
                :loading-text="$t('loadingData')"
              >
                <template v-slot:top="{}">
                  <div class="d-flex align-center">
                    <div class="ml-3" style="width: 220px;">
                      <v-text-field
                        style="width: 280px;"
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        height="5px"
                        :placeholder="$t('memberDataSection.searchSociety')"
                        dense
                        v-model="search"
                        @input="searchQuery"
                        class="shrink"
                      ></v-text-field>
                    </div>

                    <v-spacer></v-spacer>

                    <div class="d-flex align-center mb-3">
                      <div class="py-1 title">
                        {{ fromList }} -
                        {{ toList }} {{ $t('of') }}
                        {{ totalSocieties }}
                      </div>
                      <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page <= 1" @click="changePage(false)">
                        <v-icon dark>mdi-chevron-left</v-icon>
                      </v-btn>
                      <v-btn class="mx-2" fab small outlined color="primary" :disabled="options.page >= totalPages" @click="changePage(true)">
                        <v-icon dark>mdi-chevron-right</v-icon>
                      </v-btn>

                      <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                            <v-icon dark>mdi-cog-outline</v-icon>
                          </v-btn>
                        </template>
                        <v-card width="417">
                          <v-list>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title>{{ $t("deforestation.customization") }}</v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider></v-divider>
                            <div class="pl-3 pt-2 font-weight-normal">{{ $t("deforestation.recordsPerpage") }}</div>
                            <v-list-item>
                              <v-list-item-content>
                                <v-slider v-model="itemsPerPageSlider" :tick-labels="itemsPerPageOptions" :max="3" step="1" ticks="always" tick-size="4"></v-slider>
                              </v-list-item-content>
                            </v-list-item>
                            <v-divider class="mt-n3"></v-divider>
                          </v-list>
                          <v-card-text>
                            <v-container fluid>
                              <v-row>
                                <v-col class="mt-n9" cols="6" v-for="header in headers" :key="header.value">
                                  <v-checkbox
                                    v-if="header.text"
                                    v-model="selectedHeaders"
                                    :label="header.text"
                                    color="primary"
                                    :value="header.text"
                                    hide-details
                                  ></v-checkbox>
                                </v-col>
                              </v-row>
                            </v-container>
                          </v-card-text>
                          <v-layout justify-center>
                            <v-card-actions class="mb-5">
                              <v-spacer></v-spacer>
                              <v-btn outlined color="primary" @click="resetDefault" width="190">
                                <span class="text-truncate" style="max-width:150px">{{ $t("deforestation.restoreDefault") }}</span>
                              </v-btn>
                              <v-btn color="primary" @click="applyTableStructure" width="190">
                                {{ $t("deforestation.apply") }}
                              </v-btn>
                            </v-card-actions>
                          </v-layout>
                        </v-card>
                      </v-menu>
                    </div>
                  </div>
                </template>

                <template v-if="societies.length" v-slot:body="{ items }">
                  <tbody>
                    <tr
                      v-for="item in items"
                      :key="item.id"
                      style="position: relative; width: 0; height: 0"
                      @mouseover="selectedItem = item"
                      @mouseleave="selectedItem = false"
                    >
                      <template v-for="h in tableHeaders">
                        <td class="text-truncate" :key="h.value + '_nameAndId'" v-if="h.value === 'nameAndId'">
                          {{ item.fullName || [item.firstName, item.lastName].filter(Boolean).join(' ') }}
                          <br />
                          {{ item.id_number || 'N/A' }}
                        </td>

                        <td class="text-truncate" :key="h.value + '_email'" v-else-if="h.value === 'email'">
                          {{ item.email || '-' }}
                        </td>

                        <td class="text-truncate" :key="h.value + '_mobileNumber'" v-else-if="h.value === 'mobileNumber'">
                          {{ item.mobile || '-' }}
                        </td>

                        <td class="text-truncate" :key="h.value + '_address'" v-else-if="h.value === 'address'">
                          {{ item.address || '-' }}
                        </td>

                        <td class="text-truncate" :key="h.value + '_status'" v-else-if="h.value === 'status'">
                          <v-btn small rounded min-width="100" depressed class="green-button" height="40" v-if="item.status === 'active'">
                            {{ $t("memberDataSection.active") }}
                          </v-btn>
                          <v-btn small rounded min-width="100" depressed class="red-button" height="40" v-else-if="item.status === 'deactivated'">
                            {{ $t("memberDataSection.deactivated") }}
                          </v-btn>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </template>

                <template v-slot:footer></template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import BuyingStationService from "@/_services/BuyingStationService";

export default {
  name: "SocietyListing",

  created() {
    this.getSocieties();
  },

  mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
  },

  watch: {
    options: {
      handler(opt) {
        this.getSocieties(opt);
      },
      deep: true,
    },
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalSocieties / this.options.limit);
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: "/" },
        { text: this.$t("memberDataSection.societies"), disabled: true },
      ];
    },
  },

  data() {
    return {
      debounceTimer: null,
      fromList: 0,
      toList: 0,
      search: "",
      loading: true,
      selectedItem: false,
      menu: false,
      options: {
        limit: 10,
        page: 1,
        sortBy: ["id"],
        sortDesc: [true],
      },
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 1,
      selectedHeaders: [],
      tableHeaders: [],
      societies: [],
      totalSocieties: 0,
      headers: [
        {
          text: this.$t("memberDataSection.nameAndId"),
          align: "start",
          value: "nameAndId",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("memberDataSection.email"),
          align: "start",
          value: "email",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("memberDataSection.mobileNumber"),
          align: "start",
          value: "mobileNumber",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("address"),
          align: "start",
          value: "address",
          class: "black--text",
          sortable: false,
        },
        {
          text: this.$t("memberDataSection.status"),
          align: "start",
          value: "status",
          class: "black--text",
          sortable: false,
        },
      ],
      allSelectedHeaders: [
        this.$t("memberDataSection.nameAndId"),
        this.$t("memberDataSection.email"),
        this.$t("memberDataSection.mobileNumber"),
        this.$t("address"),
        this.$t("memberDataSection.status"),
      ],
    };
  },

  methods: {
    searchQuery() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.getSocieties();
      }, 500);
    },

    changePage(next) {
      if (next) {
        this.options.page += 1;
      } else {
        this.options.page -= 1;
      }
    },

    async resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
      await this.getSocieties();
    },

    async applyTableStructure() {
      this.tableHeaders = this.headers.filter((h) => this.selectedHeaders.includes(h.text));
      this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
      await this.getSocieties();
      this.menu = false;
    },

    async getSocieties(opt = null) {
      this.loading = true;
      const isDesc =
        Array.isArray(opt?.sortDesc) && opt.sortDesc.length > 0
          ? Boolean(opt.sortDesc[0])
          : true;

      const requestParams = {
        page: this.options.page || 1,
        limit: this.options.limit || 10,
        search: this.search || null,
        orderField: "id",
        order: isDesc ? "DESC" : "ASC",
      };

      try {
        const res = await BuyingStationService.listSocieties(requestParams);
        const rows = (res.data && res.data.rows) || [];

        this.societies = rows.map((s) => ({
          id: s.id,
          fullName: s.fullName || [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" "),
          firstName: s.firstName,
          lastName: s.lastName,
          id_number: s.id_number,
          email: s.email,
          mobile: s.mobile,
          address: s.address,
          status: s.active ? "active" : "deactivated",
        }));

        this.totalSocieties = (res.data && res.data.totalRows) || 0;

        this.fromList = this.totalSocieties > 0 ? (this.options.page - 1) * this.options.limit + 1 : 0;
        this.toList =
          this.options.page * this.options.limit > this.totalSocieties
            ? this.totalSocieties
            : this.options.page * this.options.limit;
      } catch (err) {
        console.error("SocietyListing fetch error:", err);
        this.$notify({ text: "Error fetching societies", type: "error" });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
</style>
